/**
 * Purchase Transaction Form Page Component
 * /admin/purchases/new and /admin/purchases/:id/edit
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiClient from '../api/client';

const PurchaseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [formData, setFormData] = useState({
    vehicle: '',
    buyer_type: '',
    buyer_investor: '',
    buyer_driver: '',
    purchase_amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    payment_status: 'pending',
    ar_transaction_id: '',
  });

  useEffect(() => {
    if (isEdit) {
      loadPurchase();
    }
    loadVehicles();
    loadInvestors();
    loadDrivers();
  }, [id]);

  const loadPurchase = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getVehiclePurchase(id);
      setFormData({
        vehicle: data.vehicle || '',
        buyer_type: data.buyer_type || '',
        buyer_investor: data.buyer_investor || '',
        buyer_driver: data.buyer_driver || '',
        purchase_amount: data.purchase_amount || '',
        transaction_date: data.transaction_date
          ? data.transaction_date.split('T')[0]
          : new Date().toISOString().split('T')[0],
        payment_status: data.payment_status || 'pending',
        ar_transaction_id: data.ar_transaction_id || '',
      });
    } catch (err) {
      setError(err.message || 'Failed to load purchase transaction');
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await apiClient.getVehicles();
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setVehicles(data);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    }
  };

  const loadInvestors = async () => {
    try {
      const response = await apiClient.getInvestors();
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setInvestors(data);
    } catch (err) {
      console.error('Failed to load investors:', err);
    }
  };

  const loadDrivers = async () => {
    try {
      // Note: You may need to add a getDrivers method to the API client
      // For now, we'll leave this empty or use a placeholder
      setDrivers([]);
    } catch (err) {
      console.error('Failed to load drivers:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear buyer fields when buyer type changes
    if (name === 'buyer_type') {
      setFormData((prev) => ({
        ...prev,
        buyer_investor: '',
        buyer_driver: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        purchase_amount: formData.purchase_amount ? parseFloat(formData.purchase_amount) : null,
        buyer_investor: formData.buyer_type === 'investor' ? formData.buyer_investor : null,
        buyer_driver: formData.buyer_type === 'driver' ? formData.buyer_driver : null,
      };

      if (isEdit) {
        await apiClient.updateVehiclePurchase(id, submitData);
      } else {
        await apiClient.createVehiclePurchase(submitData);
      }

      navigate('/admin/purchases');
    } catch (err) {
      setError(err.message || 'Failed to save purchase transaction');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/purchases')}>
            Back
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Purchase Transaction' : 'Add New Purchase Transaction'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Vehicle"
                name="vehicle"
                value={formData.vehicle}
                onChange={handleChange}
                select
                required
              >
                <MenuItem value="">Select Vehicle</MenuItem>
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.plate_number} - {vehicle.make} {vehicle.model}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Transaction Date"
                name="transaction_date"
                type="date"
                value={formData.transaction_date}
                onChange={handleChange}
                required
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Buyer Type"
                name="buyer_type"
                value={formData.buyer_type}
                onChange={handleChange}
                select
                required
              >
                <MenuItem value="">Select Buyer Type</MenuItem>
                <MenuItem value="investor">Investor</MenuItem>
                <MenuItem value="driver">Driver</MenuItem>
              </TextField>
            </Grid>
            {formData.buyer_type === 'investor' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Buyer (Investor)"
                  name="buyer_investor"
                  value={formData.buyer_investor}
                  onChange={handleChange}
                  select
                  required
                >
                  <MenuItem value="">Select Investor</MenuItem>
                  {investors.map((investor) => (
                    <MenuItem key={investor.id} value={investor.id}>
                      {investor.first_name} {investor.last_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {formData.buyer_type === 'driver' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Buyer (Driver)"
                  name="buyer_driver"
                  value={formData.buyer_driver}
                  onChange={handleChange}
                  select
                  required
                >
                  <MenuItem value="">Select Driver</MenuItem>
                  {drivers.map((driver) => (
                    <MenuItem key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Purchase Amount (AED)"
                name="purchase_amount"
                type="number"
                value={formData.purchase_amount}
                onChange={handleChange}
                required
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Payment Status"
                name="payment_status"
                value={formData.payment_status}
                onChange={handleChange}
                select
                required
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="AR Transaction ID"
                name="ar_transaction_id"
                value={formData.ar_transaction_id}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
            <Button variant="outlined" onClick={() => navigate('/admin/purchases')}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default PurchaseForm;

